import { useEffect, type ReactElement } from 'react'
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import { AppLayout } from './layouts/AppLayout.js'
import { DashboardPage } from '../pages/DashboardPage.js'
import { LoginPage } from '../pages/LoginPage.js'
import { StorePickerPage } from '../pages/StorePickerPage.js'
import { PlatformUsersPage } from '../pages/PlatformUsersPage.js'
import { PlatformUserDetailPage } from '../pages/PlatformUserDetailPage.js'
import { CustomerListPage } from '../pages/CustomerListPage.js'
import { CustomerDetailPage } from '../pages/CustomerDetailPage.js'
import { ItemDetailPage } from '../pages/ItemDetailPage.js'
import { SupplierDetailPage } from '../pages/SupplierDetailPage.js'
import { CataloguePage } from '../pages/CataloguePage.js'
import { TeamPage } from '../pages/TeamPage.js'
import { MonComptePage } from '../pages/MonComptePage.js'
import { ParametresPage } from '../pages/ParametresPage.js'
import { VitrinePage } from '../pages/VitrinePage.js'
import { CampagnesPage } from '../pages/CampagnesPage.js'
import { DepensesPage } from '../pages/DepensesPage.js'
import { DefinirMotDePassePage } from '../pages/DefinirMotDePassePage.js'
import { CoquilleSimple } from '../ui/Marque.js'
import { SalesListPage } from '../pages/SalesListPage.js'
import { AgendaPage } from '../pages/AgendaPage.js'
import { CaisseSessionPage } from '../pages/CaisseSessionPage.js'
import { CaissePage } from '../pages/CaissePage.js'
import { SaleDetailPage } from '../pages/SaleDetailPage.js'
import * as syncEngine from '../data/sync/engine.js'
import { useSessionStore } from '../stores/sessionStore.js'

function FullPageLoader(): ReactElement {
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={28} />
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3 }}>
          Restauration de votre session…
        </Typography>
      </Box>
    </Box>
  )
}

/**
 * Exige une session. Mémorise la destination pour y revenir après connexion —
 * sans quoi un lien profond partagé renverrait systématiquement à l'accueil.
 */
function RequireAuth({ children }: { children: ReactElement }): ReactElement {
  const { user, loading } = useSessionStore()
  const location = useLocation()

  if (loading) return <FullPageLoader />
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }
  return children
}

/**
 * Vérifie que le slug de l'URL correspond à une boutique de l'utilisateur.
 *
 * Le serveur refuse déjà l'accès (404) — cette garde évite simplement
 * d'afficher une coquille vide le temps de l'aller-retour.
 */
function RequireStore({ children }: { children: ReactElement }): ReactElement {
  const { slug } = useParams<{ slug: string }>()
  const { storeBySlug, rememberStore, stores } = useSessionStore()
  const store = slug ? storeBySlug(slug) : undefined

  useEffect(() => {
    if (store) rememberStore(store.slug)
  }, [store, rememberStore])

  // ⭐ Ouvre la réplique locale de CETTE boutique et lance la boucle de
  // synchronisation. Changer de boutique arrête la précédente : deux moteurs
  // concurrents se disputeraient la file d'attente.
  useEffect(() => {
    if (!store) return
    syncEngine.start(store.storeId)
    return () => syncEngine.stop()
  }, [store])

  if (!slug) return <Navigate to="/s" replace />
  if (!store) {
    // Slug inconnu : on renvoie au sélecteur plutôt que d'afficher une erreur,
    // le cas le plus fréquent étant un signet devenu obsolète.
    return <Navigate to="/s" replace />
  }
  if (stores.length === 0) return <FullPageLoader />
  return children
}

/** L'ancienne adresse des fournisseurs mène désormais à l'onglet du catalogue. */
function RedirectFournisseurs(): ReactElement {
  const { slug } = useParams<{ slug: string }>()
  return <Navigate to={`/s/${slug}/catalogue/fournisseurs`} replace />
}

/** Redirige vers la dernière boutique ouverte, ou vers le sélecteur. */
function HomeRedirect(): ReactElement {
  const { stores, lastStore } = useSessionStore()
  const remembered = lastStore()

  if (remembered && stores.some((s) => s.slug === remembered)) {
    return <Navigate to={`/s/${remembered}/dashboard`} replace />
  }
  // Une seule boutique : inutile de faire choisir l'utilisateur.
  if (stores.length === 1) {
    return <Navigate to={`/s/${stores[0]!.slug}/dashboard`} replace />
  }
  return <Navigate to="/s" replace />
}

/** Ancienne adresse des utilisateurs, conservée pour les liens déjà écrits. */
function RedirigeVersUtilisateurs(): ReactElement {
  const { slug } = useParams<{ slug: string }>()
  return <Navigate to={`/s/${slug}/utilisateurs`} replace />
}

function LoginRoute(): ReactElement {
  const { user, loading } = useSessionStore()
  if (loading) return <FullPageLoader />
  if (user) return <Navigate to="/" replace />
  return (
    <CoquilleSimple>
      <LoginPage />
    </CoquilleSimple>
  )
}

export function AppRouter(): ReactElement {
  const restore = useSessionStore((s) => s.restore)

  useEffect(() => {
    // Rejoue le cookie httpOnly : recharger la page ne doit pas déconnecter.
    void restore()
  }, [restore])

  return (
    <Router>
      <Routes>
        {/* ⭐ Le SITE VITRINE, entièrement public : ni session, ni réplique,
            ni menu d'application. C'est la page qu'un client lit avant d'être
            client — l'envelopper dans une garde d'authentification aurait
            renvoyé chaque visiteur sur un formulaire de connexion. */}
        <Route path="/boutique/:slug" element={<VitrinePage />} />

        {/* ⭐ La PRÉVISUALISATION, brouillon compris — réservée aux connectés.
            Deux adresses pour deux arrivées : le gérant par le slug de sa
            boutique, l'exploitant par l'identifiant d'une enseigne dont il
            n'est pas membre. */}
        <Route
          path="/s/:slug/apercu"
          element={
            <RequireAuth>
              <VitrinePage apercu />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/vitrine/:storeId"
          element={
            <RequireAuth>
              <VitrinePage apercu />
            </RequireAuth>
          }
        />

        <Route path="/login" element={<LoginRoute />} />

        {/* ⭐ Connexion PAR BOUTIQUE. C'est ici qu'entre l'équipe : son compte
            vit dans le schéma de l'enseigne, et le serveur a besoin de savoir
            laquelle avant même de savoir qui se présente.
            ⚠️ PUBLIQUE, comme /login : exiger une session pour atteindre
            l'écran de connexion enfermerait dehors ceux qui n'en ont pas. */}
        <Route path="/s/:slug/login" element={<LoginRoute />} />

        <Route
          path="/"
          element={
            <RequireAuth>
              <HomeRedirect />
            </RequireAuth>
          }
        />

        {/* PUBLIQUE : la personne n'a pas encore de mot de passe, donc pas de
            session. Le jeton de l'URL fait preuve. */}
        <Route path="/definir-mot-de-passe" element={<CoquilleSimple><DefinirMotDePassePage /></CoquilleSimple>} />

        <Route
          path="/s"
          element={
            <RequireAuth>
              <CoquilleSimple><StorePickerPage /></CoquilleSimple>
            </RequireAuth>
          }
        />

        {/* Console plateforme. Hors de /s/ : ces écrans ne dépendent d'AUCUNE
            boutique — c'est précisément leur raison d'être. */}
        <Route
          path="/admin/utilisateurs"
          element={
            <RequireAuth>
              <CoquilleSimple><PlatformUsersPage /></CoquilleSimple>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/utilisateurs/:id"
          element={
            <RequireAuth>
              <CoquilleSimple><PlatformUserDetailPage /></CoquilleSimple>
            </RequireAuth>
          }
        />

        {/* ADR-05 : préfixe /s/ pour isoler l'espace de noms des boutiques
            des routes applicatives (/login, /api, /admin). */}
        <Route
          path="/s/:slug/dashboard"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <DashboardPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />

        <Route
          path="/s/:slug/clients"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <CustomerListPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />

        <Route
          path="/s/:slug/clients/:id"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <CustomerDetailPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />

        {/* Agenda — l'entrée du menu existait depuis le premier sprint et ne
            menait nulle part : le clic retombait sur la route « * » et
            renvoyait à l'accueil, sans que rien ne l'explique. */}
        <Route
          path="/s/:slug/agenda"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <AgendaPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />

        {/* Catalogue — produits et services partagent la même page,
            différenciée par `kind` (ADR-06). */}
        <Route
          path="/s/:slug/campagnes"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <CampagnesPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/depenses"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <DepensesPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/ventes"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <SalesListPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/ventes/:id"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <SaleDetailPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        {/* ⭐ La GESTION de la caisse — ouverture, fonds, clôture — vit DANS
            la coquille, à la différence de l'écran d'encaissement. Ce sont
            deux gestes que tout oppose : celui-ci se tient deux fois par
            jour, au calme, par qui répond de l'argent du tiroir. */}
        <Route
          path="/s/:slug/tiroir"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <CaisseSessionPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        {/* ⚠️ La caisse vit HORS de la coquille : ni barre latérale, ni fil
            d'Ariane. Un caissier qui clique par mégarde sur « Rapports » en
            pleine vente perdrait son panier ; la sortie doit être un geste
            unique et explicite. */}
        <Route
          path="/s/:slug/caisse"
          element={
            <RequireAuth>
              <RequireStore>
                <CaissePage />
              </RequireStore>
            </RequireAuth>
          }
        />
        {/* ⭐ Le MÊME écran que la caisse, en mode devis : composer un panier
            est le même geste, qu'on encaisse ou qu'on chiffre. Il vit donc
            aussi hors de la coquille. */}
        <Route
          path="/s/:slug/devis"
          element={
            <RequireAuth>
              <RequireStore>
                <CaissePage mode="devis" />
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/commande"
          element={
            <RequireAuth>
              <RequireStore>
                <CaissePage mode="commande" />
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/catalogue"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <CataloguePage onglet="articles" />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/catalogue/categories"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <CataloguePage onglet="categories" />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/catalogue/tva"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <CataloguePage onglet="tva" />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/catalogue/fournisseurs"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <CataloguePage onglet="fournisseurs" />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/catalogue/:id"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <ItemDetailPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        {/* Ancienne adresse conservée : les signets et les liens déjà
            partagés ne doivent pas tomber dans le vide. */}
        <Route
          path="/s/:slug/fournisseurs"
          element={<RedirectFournisseurs />}
        />
        <Route
          path="/s/:slug/mon-compte"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <MonComptePage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        {/* ⭐ `/s/:slug/utilisateurs` — l'adresse qu'on tape naturellement, et
            celle du menu. Les utilisateurs ne sont pas un réglage parmi
            d'autres : c'est un écran qu'on ouvre pour lui-même. */}
        <Route
          path="/s/:slug/utilisateurs"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <TeamPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />

        {/* ⚠️ L'ANCIENNE adresse continue de mener au même écran. Elle est
            encore écrite en dur dans la console plateforme, et se trouve dans
            les favoris de qui utilise l'application depuis des semaines : la
            supprimer aurait renvoyé ces liens vers l'accueil, sans rien dire —
            exactement ce qui se produisait pour `/s/:slug/utilisateurs`. */}
        <Route
          path="/s/:slug/parametres/utilisateurs"
          element={<RedirigeVersUtilisateurs />}
        />
        {/* ⭐ Le module Paramètres, et ses onglets dans l'ADRESSE.
            `/parametres/synchronisation` continue donc de mener exactement où
            il menait : c'est l'adresse citée dans les messages d'erreur de
            synchronisation, et celle qui dort dans les favoris. */}
        <Route
          path="/s/:slug/parametres"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <ParametresPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/parametres/:onglet"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <ParametresPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />
        <Route
          path="/s/:slug/catalogue/fournisseurs/:id"
          element={
            <RequireAuth>
              <RequireStore>
                <AppLayout>
                  <SupplierDetailPage />
                </AppLayout>
              </RequireStore>
            </RequireAuth>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  )
}
